import {useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {Alert, Stack} from '@mantine/core';
import {I18n} from '@aws-amplify/core';
import {FormPageLayout, formatPerson, PlatformLoader} from '@flexinet/ui-components';

import {Profile} from '../profile/Profile';
import {EditPageNavigation} from '../navigation/EditPageNavigation';
import {useUser} from '../hooks/useUser';
import {useUsersSelection} from '../hooks/useUsersSelection';
import {useEditPageTools} from '../hooks/useEditPageTools';
import config from '../config';

export const EditPage = () => {
    const {id: userId} = useParams();

    if (typeof userId === 'undefined') {
        throw new Error('userId is required parameter');
    }

    const {record: user, loading, isError} = useUser(userId);
    const {handleClearSelection} = useUsersSelection();
    const {tools} = useEditPageTools();

    useEffect(() => {
        handleClearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only effect: deliberately runs once, adding deps would re-fire it
    }, []);

    if (loading) {
        return <PlatformLoader message={I18n.get('Loading user...')}/>;
    }

    // The console's copy showed the loader forever when the person could not be
    // loaded. Say so instead.
    if (!user) {
        return (
            <Alert color='red' title={isError ? I18n.get('Could not load this user') : I18n.get('User not found')} m='md'>
                {isError
                    ? I18n.get('users-api did not return this person. Try again from the users list.')
                    : I18n.get('Nobody in this directory has that id.')}
            </Alert>
        );
    }

    return (
        <FormPageLayout
            // Singular title plus who it is (checklist §2, headers).
            title={`${I18n.get(config.titles.singular)}: ${formatPerson(user) ?? user.id}`}
            pageNavigation={<EditPageNavigation component={user}/>}
            tools={tools}
        >
            <Stack gap='lg'>
                <Profile record={user}/>
            </Stack>
        </FormPageLayout>
    );
};
